import React, { useState, useMemo, useEffect, useRef } from "react";
import "./MutualFunds.css";
import { processData } from "./MutualFundAlert";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import { FaSort, FaInfoCircle } from "react-icons/fa";
import SearchIcon from "@mui/icons-material/Search";
const MutualFundTable = ({ mutualFunds, mutualFundsAlerts }) => {
  const { chartData, alerts } = processData(mutualFundsAlerts);
  const latestDate = Object.keys(mutualFunds).sort().reverse()[0];
  const latestData = mutualFunds[latestDate];

  const [sortOrder, setSortOrder] = useState("highest"); // AUM sort order
  const [fundSizeSortOrder, setFundSizeSortOrder] = useState("highest"); // Fund size sort order
  const [percentageSortOrder, setPercentageSortOrder] = useState("highest"); // % Change sort order
  const [dateSortOrder, setDateSortOrder] = useState("latest"); // Date sort order
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [selectAll, setSelectAll] = useState(false); // State for "Select All"
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [sortColumn, setSortColumn] = useState("AUM"); // Track active sorting column
  const [showModal, setShowModal] = useState(false); // State for column info modal

  const graphRef = useRef(null); // Ref for the graph component

  // Function to find alerts for a specific fund
  const findAlertForFund = (fundName) => {
    const trimmedFundName = fundName.trim();
    const alert = alerts.find((alert) => alert.includes(trimmedFundName));

    if (alert) {
      const parts = alert.split(" ");
      const date = parts[1]; // Extract the date from the alert
      const previousValue = parseFloat(parts[parts.length - 3]); // Previous value
      const currentValue = parseFloat(parts[parts.length - 1]); // Current value

      const absoluteChange = (currentValue - previousValue).toFixed(2); // Calculate absolute change
      const changeColor = currentValue > previousValue ? "green" : "red"; // Determine color based on change

      const arrow =
        currentValue > previousValue ? (
          <span style={{ color: "green", fontSize: "21px" }}>↑</span>
        ) : (
          <span style={{ color: "red", fontSize: "21px" }}>↓</span>
        );

      const currentValueColor = currentValue > previousValue ? "green" : "red";
      const message = alert.replace(`${date} : ${fundName.trim()} `, "");

      return {
        date,
        absoluteChange, // Return absolute change
        movement: `${previousValue}% →  ${currentValue}%`,
        arrow,
        changeColor, // Return the color for styling % Change
        message: (
          <>
            {arrow}
            <span style={{ fontWeight: "bold", marginRight: "5px" }}>
              {date}
            </span>
            :{" "}
            {message.split(`${currentValue}%`).map((part, index) => (
              <React.Fragment key={index}>
                {part}
                {index < message.split(`${currentValue}%`).length - 1 && (
                  <span style={{ color: currentValueColor, fontWeight: "500" }}>
                    {currentValue}%
                  </span>
                )}
              </React.Fragment>
            ))}
          </>
        ),
      };
    }

    return {
      date: "",
      absoluteChange: "-",
      movement: "-",
      changeColor: "black",
      message: "No alerts",
    };
  };

  // Handle AUM sorting toggle
  const handleAumSortToggle = () => {
    setSortOrder((prevOrder) =>
      prevOrder === "highest" ? "lowest" : "highest",
    );
    setSortColumn("AUM");
  };
  const handleFundSizeSortToggle = () => {
    setFundSizeSortOrder((prevOrder) =>
      prevOrder === "highest" ? "lowest" : "highest",
    );
    setSortColumn("Fund Size");
  };

  // Handle % Change sorting toggle
  const handlePercentageSortToggle = () => {
    setPercentageSortOrder((prevOrder) =>
      prevOrder === "highest" ? "lowest" : "highest",
    );
    setSortColumn("% Change");
  };

  // Handle Date sorting toggle
  const handleDateSortToggle = () => {
    setDateSortOrder((prevOrder) =>
      prevOrder === "latest" ? "oldest" : "latest",
    );
    setSortColumn("Date");
  };

  // Sorting logic based on the active sorting column (AUM, % Change, or Date)
  // Sorting logic based on the active sorting column (AUM, % Change, or Date)
  const sortedData = useMemo(() => {
    if (latestData) {
      let sortedArray = [...latestData];

      if (sortColumn === "AUM") {
        // Sorting by AUM
        sortedArray = sortedArray.sort((a, b) => {
          const diff = parseFloat(a.percentage) - parseFloat(b.percentage);
          return sortOrder === "highest" ? -diff : diff;
        });
      } else if (sortColumn === "% Change") {
        // Sorting by % Change
        sortedArray = sortedArray.sort((a, b) => {
          const alertA = findAlertForFund(a.mfName);
          const alertB = findAlertForFund(b.mfName);

          const diff =
            parseFloat(alertA.absoluteChange) -
            parseFloat(alertB.absoluteChange);
          return percentageSortOrder === "highest" ? -diff : diff;
        });
      } else if (sortColumn === "Fund Size") {
        // Sorting by Fund Size
        sortedArray = sortedArray.sort((a, b) => {
          const sizeA = parseFloat(a.fund_size); // Ensure fund_size is parsed as a float
          const sizeB = parseFloat(b.fund_size);
          return fundSizeSortOrder === "highest"
            ? sizeB - sizeA
            : sizeA - sizeB;
        });
      } else if (sortColumn === "Date") {
        // Sorting by Date
        sortedArray = sortedArray.sort((a, b) => {
          const alertA = findAlertForFund(a.mfName);
          const alertB = findAlertForFund(b.mfName);

          const dateA = alertA.date ? new Date(alertA.date) : null;
          const dateB = alertB.date ? new Date(alertB.date) : null;

          // Sort logic for dates
          if (!dateA && !dateB) return 0; // Both are missing
          if (!dateA) return 1; // Date A is missing, put it last
          if (!dateB) return -1; // Date B is missing, put it last

          return dateSortOrder === "latest" ? dateB - dateA : dateA - dateB;
        });
      }

      // Apply search filter
      return sortedArray.filter(
        (mf) =>
          mf.mfName &&
          mf.mfName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    return [];
  }, [
    latestData,
    sortOrder,
    percentageSortOrder,
    dateSortOrder,
    fundSizeSortOrder,
    sortColumn,
    searchTerm,
  ]);

  const handleCheckboxChange = (fundName) => {
    if (selectedFunds.includes(fundName)) {
      setSelectedFunds((prevSelectedFunds) =>
        prevSelectedFunds.filter((fund) => fund !== fundName),
      );
    } else {
      setSelectedFunds((prevSelectedFunds) => [...prevSelectedFunds, fundName]);

      setTimeout(() => {
        if (graphRef.current) {
          graphRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100); // Adjust the timeout value as needed
    }
  };

  const handleSelectAllChange = () => {
    const allFunds = latestData.map((mf) => mf.mfName);
    if (selectAll) {
      setSelectedFunds([]); // Deselect all funds
    } else {
      setSelectedFunds(allFunds); // Select all funds
    }
    setSelectAll(!selectAll); // Toggle the "Select All" checkbox

    setTimeout(() => {
      if (graphRef.current) {
        graphRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100); // Adjust the timeout value as needed
  };

  const filteredChartData = useMemo(() => {
    if (selectedFunds.length > 0) {
      const trimmedSelectedFunds = selectedFunds.map((fund) => fund.trim());
      const filteredData = {
        labels: chartData.labels,
        datasets: chartData.datasets.filter((dataset) =>
          trimmedSelectedFunds.includes(dataset.label.trim()),
        ),
      };
      return filteredData;
    }
    return null; // If no fund is selected, return null
  }, [selectedFunds, chartData]);

  useEffect(() => {}, [filteredChartData, chartData, selectedFunds]);

  //Disable background scroll when modal is open
 useEffect(() => {
  if (showModal) {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden"; 
  } else {
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
  }

  return () => {
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
  };
}, [showModal]);

  return (
    <>
      {latestData && latestData !== undefined ? (
        <>
          <div className="mutual-funds-content">
            <div className="mutual-funds-header">
              <div className="mutual-funds-header-left">
                <h2>Mutual Funds Holding</h2>
              </div>
              <div className="mutual-funds-header-right">
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <SearchIcon className="search-icon" />
                    <input
                      className="search-mf-input"
                      type="text"
                      placeholder="Search Mutual Funds..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <button
                    className="info-icon-btn"
                    onClick={() => setShowModal(!showModal)}
                    title="View column information"
                  >
                    <FaInfoCircle />
                  </button>
                </div>
              </div>
            </div>

            {showModal && (
              <div className="ci-modal-wrap">
                <div className="ci-overlay" onClick={() => setShowModal(false)} />
                <div className="ci-panel">
                  <div className="ci-header">
                    <span className="ci-header-title">Column Information</span>
                    <button className="ci-close" onClick={() => setShowModal(false)} aria-label="Close">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <div className="ci-body">
                    {[
                      { col: "MF Names",       desc: "The full name of the Mutual Fund scheme holding a position in this stock." },
                      { col: "Total AUM (crs)", desc: "Assets Under Management in Crores — the total market value of all assets managed by the fund." },
                      { col: "AUM %",           desc: "The percentage of the fund's total AUM allocated specifically to this stock." },
                      { col: "Date",            desc: "The date on which the most recent change or alert was recorded for this fund." },
                      { col: "% Change",        desc: "Change in AUM allocation vs the previous period. Green = increase, Red = decrease." },
                      { col: "Movement",        desc: "Shows the shift in allocation: previous value → current value (both as percentages)." },
                    ].map(({ col, desc }) => (
                      <div className="ci-item" key={col}>
                        <div className="ci-item-col">{col}</div>
                        <div className="ci-item-desc">{desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="mutual-funds-table">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>
                      <input
                        style={{ height: "18px", width: "18px" }}
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                      />
                    </th>
                    <th style={{ width: "60px", textAlign: "center" }}>S.No</th>
                    <th>MF Names</th>
                    <th
                      onClick={handleFundSizeSortToggle}
                      style={{ cursor: "pointer" }}
                    >
                      Total AUM (crs) <FaSort />
                    </th>
                    <th
                      onClick={handleAumSortToggle}
                      style={{ cursor: "pointer" }}
                    >
                      AUM <FaSort />
                    </th>
                    <th
                      onClick={handleDateSortToggle}
                      style={{ cursor: "pointer" }}
                    >
                      Date <FaSort />
                    </th>
                    <th
                      onClick={handlePercentageSortToggle}
                      style={{ cursor: "pointer" }}
                    >
                      % Change <FaSort />
                    </th>
                    <th>Movement</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((mf, index) => {
                    const alertInfo = findAlertForFund(mf.mfName);
                    return (
                      mf.instrument !== "-" && (
                        <tr key={index}>
                          <td>
                            <input
                              style={{ height: "18px", width: "18px" }}
                              type="checkbox"
                              checked={selectedFunds.includes(mf.mfName)}
                              onChange={() => handleCheckboxChange(mf.mfName)}
                            />
                          </td>
                          {/* Serial Number */}
                          <td style={{ width: "60px", textAlign: "center" }}>{index + 1}</td>
                          <td>{mf.mfName}</td>
                          <td>{mf.fund_size}</td>
                          <td>{mf.percentage}</td>
                          <td>{alertInfo.date}</td>
                          <td
                            style={{
                              color: alertInfo.changeColor,
                              fontWeight: "bold",
                            }}
                          >
                            {alertInfo.arrow} {alertInfo.absoluteChange}
                          </td>
                          <td>{alertInfo.movement}</td>
                        </tr>
                      )
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selectedFunds.length === 0 && (
              <div className="mutual-funds-table-tip">
                <FaInfoCircle className="tip-icon" />
                <span>Select checkboxes next to Mutual Funds to view and compare their historical holdings graphically below.</span>
              </div>
            )}
          </div>
          {selectedFunds.length > 0 && (
            <div className="mutual-funds-graph-wrapper" ref={graphRef}>
              <h3>Graphical Analysis</h3>
              {filteredChartData && filteredChartData.datasets.length > 0 && (
                <Line
                  data={filteredChartData}
                  height={400}
                  width={1200}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    spanGaps: true,
                    plugins: {
                      legend: {
                        display: window.innerWidth <= 768 ? false : true,
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          pointStyle: "circle",
                          padding: 20,
                          font: {
                            size: 12,
                            weight: "500",
                          },
                          color: "#374151",
                          boxWidth: 8,
                          boxHeight: 8,
                        },
                      },
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: "Date",
                          font: {
                            size: 13,
                            weight: "600",
                          },
                        },
                        ticks: {
                          display: window.innerWidth > 768,
                          minRotation: 45,
                          maxRotation: 45,
                          font: {
                            size: 10,
                          },
                        },
                        grid: {
                          drawBorder: false,
                          color: "rgba(0, 0, 0, 0.05)",
                        },
                      },
                      y: {
                        title: {
                          display: true,
                          text: "% Value",
                          font: {
                            size: 13,
                            weight: "600",
                          },
                        },
                        ticks: {
                          callback: function (value) {
                            return value + "%";
                          },
                          font: {
                            size: 10,
                          },
                        },
                        grid: {
                          drawBorder: false,
                          color: "rgba(0, 0, 0, 0.05)",
                        },
                      },
                    },
                  }}
                />
              )}
            </div>
          )}
        </>
      ) : (
        <div className="no-data-alerts-graph">
          No Mutual Fund Data Available
        </div>
      )}
    </>
  );
};

export default MutualFundTable;
